import { useState } from 'react';

export default function SupportDetails() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const supportItems = [
    {
      icon: 'ri-compass-line',
      title: '住宅購入・建築の方向性設計',
      description: `ご家族構成、将来設計、資金計画を踏まえ、
「どの選択肢が合っているか」を整理します。
感覚ではなく、判断軸を明確にします。`
    },
    {
      icon: 'ri-home-2-line',
      title: 'ハウスメーカー・工務店選定支援',
      description: `価格・性能・担当者の違いを客観的に整理し、
比較検討をサポートします。
営業トークに流されない判断を行います。`
    },
    {
      icon: 'ri-map-pin-line',
      title: '土地・不動産の見極めサポート',
      description: `立地、価格、将来性、リスクを多角的に確認。
「買ってから後悔しない」ための視点を提供します。`
    },
    {
      icon: 'ri-calculator-line',
      title: '住宅ローン・資金計画コンサルティング',
      description: `借りられるかではなく、返し続けられるか。
将来を見据えた現実的な資金計画を設計します。`
    },
    {
      icon: 'ri-file-list-3-line',
      title: '契約前の意思決定チェック',
      description: `契約内容・条件・進め方を第三者視点で確認。
不安を残したまま進まないための最終確認です。`
    },
    {
      icon: 'ri-calendar-line',
      title: '次のアクション設計と実行支援',
      description: `決断後の動き方まで具体化。
「何を、いつ、どう進めるか」を明確にします。`
    }
  ];

  const actualSupportItems = [
    {
      icon: 'ri-checkbox-circle-line',
      title: '進め方と判断基準が明確になる',
      description: `全体の流れと意思決定ポイントが整理され、
「今、何を決めるべきか」を迷わず判断できるようになります。`
    },
    {
      icon: 'ri-checkbox-circle-line',
      title: '住宅会社選びに納得感が生まれる',
      description: `比較ポイントと注意点が明確になり、
価格や営業トークに振り回されず選べるようになります。`
    },
    {
      icon: 'ri-checkbox-circle-line',
      title: '資金面の不安を前提に判断できる',
      description: `ローンや将来支出を踏まえた現実的な視点で、
無理のない選択肢を判断できるようになります。`
    },
    {
      icon: 'ri-checkbox-circle-line',
      title: '最終決断に自信が持てる',
      description: `選択肢を客観的に比較したうえで、
「この判断で進む」と腹落ちした決断ができます。`
    }
  ];

  const toggleAccordion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-20 px-6 bg-white">
      <div className="max-w-[1200px] mx-auto">
        <h2 className="text-3xl font-bold text-[#5A4033] text-center mb-16">
          具体的なサポート内容
        </h2>

        {/* PC版: 2行×3列グリッド */}
        <div className="hidden md:grid grid-cols-3 gap-8 mb-12">
          {supportItems.map((item, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl p-6 shadow-md hover:shadow-lg hover:scale-[1.03] transition-all duration-250 ease-out"
            >
              <div className="w-14 h-14 bg-[#F2A67E]/10 rounded-full flex items-center justify-center mb-4 mx-auto">
                <i className={`${item.icon} text-[#F2A67E] text-3xl`}></i>
              </div>
              <h3 className="text-[#5A4033] font-bold text-center mb-4">
                {item.title}
              </h3>
              <p className="text-[#5A4033] text-sm leading-relaxed whitespace-pre-line">
                {item.description}
              </p>
            </div>
          ))}
        </div>

        {/* スマホ版: アコーディオン */}
        <div className="md:hidden space-y-4 mb-12">
          {supportItems.map((item, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl shadow-md overflow-hidden"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full px-6 py-5 flex items-center justify-between text-left cursor-pointer hover:bg-gray-50 transition-colors"
              >
                <h3 className="text-[#5A4033] font-bold flex-1 pr-4">
                  {item.title}
                </h3>
                <i
                  className={`text-[#F2A67E] text-xl transition-transform duration-300 ${
                    openIndex === index ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'
                  }`}
                ></i>
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-[500px]' : 'max-h-0'
                }`}
              >
                <div className="px-6 pb-5">
                  <p className="text-[#5A4033] text-sm leading-relaxed whitespace-pre-line">
                    {item.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 実際にしていること 4カード */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {actualSupportItems.map((item, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl p-6 shadow-md hover:shadow-lg hover:scale-[1.03] transition-all duration-250 ease-out"
            >
              <div className="w-14 h-14 bg-[#F2A67E]/10 rounded-full flex items-center justify-center mb-4 mx-auto">
                <i className={`${item.icon} text-[#F2A67E] text-3xl`}></i>
              </div>
              <h3 className="text-[#5A4033] font-bold text-center mb-3 text-sm">
                {item.title}
              </h3>
              <p className="text-[#5A4033] text-xs leading-relaxed text-center whitespace-pre-line">
                {item.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}